/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { IPlanMilestone } from '../../../../modules/plan/services/milestone-line.service';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { cssVar } from '@touchplan/css-var';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { combineLatest } from 'rxjs';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
export class AheadBehindDrawer extends AbstractAreaDrawer {
	/**
	 * We store the last Milestone as a cache to handle cases where the canvas is forced to redraw
	 * @private
	 */
	private _cachedMilestone: IPlanMilestone | null = null;
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.BACKGROUND_VISUALS_MILESTONE_AHEAD_BEHIND);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}
	/**
	 * Draw the Ahead or Behind background visual
	 * @param milestone
	 */
	public draw(milestone: IPlanMilestone | null = this._cachedMilestone): void {
		// We use 0 here because of the plan is not ahead nor behind schedule, then it is on track and we don't draw anything.
		if (!milestone || milestone.daysBehind === 0) {
			this.hide();
			return;
		}
		// Since we are drawing in screen space, we get the screen space equivalent from the world targetX and world scheduleX
		// then calculate the difference between them to get our width (in screen space units)
		const width: number = Math.abs(
			this.parentApplication.camera.realToScreenX(milestone.targetX) -
				this.parentApplication.camera.realToScreenX(milestone.scheduleX)
		);
		const height: number = this.parentApplication.camera.view.height;
		super.draw();
		this._graphics.beginFill(this.getColor(milestone));
		this._graphics.drawRect(0, 0, width, height);
		this._graphics.endFill();
		this._cachedMilestone = milestone;
		// Update at the end because this sets new positions.
		this.update();
	}

	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public update(_?: number): void {
		if (!this._cachedMilestone) {
			return;
		}
		const data: IPlanMilestone = this._cachedMilestone;
		const anchorValue: number = data.daysBehind < 0 ? data.scheduleX : data.targetX;
		// Using a 0 value here to position at the top of the screen
		super.update(this.parentApplication.camera.realToScreenX(anchorValue), 0);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.includeChildren): void {
		super.destroy(options);
		this._canvasAngularLand = null;
	}

	/**
	 * Helper method which returns the expected
	 * @param milestone
	 * @private
	 */
	private getColor(milestone: IPlanMilestone): string {
		// BEGIN PLACEHOLDER: Replace as part of TP-6864
		return milestone.daysBehind > 0 ? cssVar.planBehindLegacy : cssVar.planAheadLegacy;
		// END PLACEHOLDER
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		combineLatest([
			this.parentApplication.camera.changed$,
			this._canvasAngularLand.milestoneLineService.changed$,
		])
			.pipe(
				takeUntil(this._shutdownSubject$),
				distinctUntilChanged(this.isSame),
				map(([changedEvent, milestone]: [ViewportChangedEvent, IPlanMilestone]) => milestone)
			)
			.subscribe((milestone: IPlanMilestone) => this.draw(milestone));
	}

	/**
	 * We only want to re-draw our background area in the following cases:
	 * - Viewport Scale changes because this means the projected width has changed
	 * - If the Observed data changes
	 * This method acts as a helper for our Rx operator to make the entire thing easier to read.
	 * @param previous
	 * @param current
	 * @private
	 */
	private isSame(
		previous: [ViewportChangedEvent, IPlanMilestone],
		current: [ViewportChangedEvent, IPlanMilestone]
	): boolean {
		return (
			previous[0].scale.x === current[0].lastScale.x &&
			previous[0].scale.y === current[0].lastScale.y &&
			previous[1] === current[1]
		);
	}
}
